<template>
  <div class="catalog-content-area">
    <div class="catalog-content-area__add-group-row">
      <button class="btn btn-secondary catalog-content-area__add-group-btn" @click="createContentGroup()">
        <i class="uil-plus"></i> {{ $t('general.add_content_group') }}
      </button>
    </div>
    <div v-for="(content, index) in form.contents" :key="content.id" class="catalog-content-area__section">
      <ul class="c-curriculum" uk-accordion>
        <li class="uk-open">
          <div class="catalog-section-header">
            <div class="catalog-section-header__row">
              <div class="catalog-section-header__icon">
                <i class="uil-folder"></i>
              </div>
              <div class="catalog-section-header__name">
                <a class="uk-accordion-title" href="#">{{ content.name }}</a>
              </div>
              <div class="catalog-section-header__actions">
                <a class="btn btn-info btn-sm catalog-section-header__action-btn" @click="toggleModal(content.id)">
                  <i class="uil-plus"></i> {{ $t('general.creating_content') }}
                </a>
                <a class="btn btn-sm btn-danger catalog-section-header__action-btn catalog-section-header__action-btn--delete" @click="deleteGroupById(content.id)">
                  <i class="uil-trash"></i>
                </a>
              </div>
            </div>
          </div>
          <div class="uk-accordion-content">
            <ul class="sec-list" uk-sortable="handle: .uk-sortable-handle">
              <li v-for="(education, educationIndex) in content.educations" :key="education.id || educationIndex">
                <education-item-card :content-type-name="getContentTypeById(education.training_file_type_id || education.content_type_id)?.name"
                                     :education="education"
                                     :education-index="educationIndex"
                                     @delete="deleteEducation(index, educationIndex)"
                                     @edit="openEditModal(content.id, educationIndex)"
                />
              </li>
            </ul>
          </div>
        </li>
      </ul>

      <AppModal :title="isReadOnly ? (currentEducation?.name + ' - ' + $t('general.preview')) : (isEditing ? (currentEducation?.name + ' ' + $t('general.edit')) : $t('general.add_content'))"
                :visible="activeModalId === content.id" width="75%" max-height="90vh" @close="closeModal">

        <div class="catalog-modal-form">
          <div class="catalog-modal-form__row">
            <div class="catalog-modal-form__sidebar">
              <div class="catalog-modal-field">
                <div class="catalog-modal-field__inner">
                  <label class="catalog-modal-field__label" for="contentTypes">
                    {{ $t('general.content_type') }}
                    <span v-if="!isEditing" class="required">*</span>
                  </label>
                    <select v-if="isEditing" :value="currentContentTypeId"
                            class="selectpickerFileCategory bg-solid" disabled>
                      <option value="">{{ $t('collectionDetail.content_type_placeholder') }}</option>
                      <option v-for="item in formattedContentTypes" :key="item.id" :value="item.id">
                        {{ item.name }}
                      </option>
                    </select>
                    <multiple-select-input v-else
                                           :allowEmpty="false"
                                           :model.sync="content_type_id"
                                           :multiple="false"
                                           :required="false"
                                           :searchable="false"
                                           :showLabels="false"
                                           :values="formattedContentTypes"
                                           @select="content_type_id = $event; changeOption(content_type_id)"/>
                </div>
              </div>
              <sf-input v-model="currentEducationName"
                        :label="$t('general.content_title')"
                        :placeholder="$t('general.content_title')"
                        :required="true"
                        name="contentName"
                        type="text"
              />
              <div v-if="showScormArea" class="form-group d-flex align-items-center">
                <label class="mr-3">{{ $t('general.use_scorm_content_score') }}:</label>
                <toggle-button v-model="currentEducationUseContentPoint"
                               color="#ae71ff"/>
              </div>
              <sf-input v-if="!currentEducationUseContentPoint"
                        v-model="currentEducationScore"
                        :label="$t('general.content_score') + ' ' + $t('general.content_score_min_max')"
                        :max="100"
                        :min="0"
                        :placeholder="$t('general.content_score')"
                        :required="true"
                        name="score"
                        type="number"
              />
              <sf-input v-model="currentEducationDuration"
                        :label="$t('general.duration')"
                        :placeholder="$t('general.duration')"
                        :required="true"
                        name="duration"
                        type="number"
              />
              <div class="form-group d-flex align-items-center">
                <label class="mr-3">{{ $t('general.demo') }}:</label>
                <toggle-button v-model="currentEducationIsDemo"
                               color="#ae71ff"/>
              </div>
            </div>
            <div class="catalog-modal-form__content">
              <h4 class="catalog-modal-form__upload-title">
                <span v-if="currentContentType">{{ currentContentType.name }}</span>
                <span v-if="!isEditing">{{ $t('general.upload') }} <span class="required">*</span></span>
                <span v-if="isReadOnly" class="badge badge-secondary ml-2">{{ $t('general.preview') }}</span>
              </h4>

              <template v-if="isReadOnly">
                <div class="content-preview-area">
                  <template v-if="showFileUpload && currentEducation">
                    <div v-if="currentContentTypeId === 2 && currentEducation.file" class="preview-item">
                      <img :src="getFileUrl(currentEducation.file)"
                           class="img-fluid"
                           style="max-height: 300px; object-fit: contain; border-radius: 8px;">
                      <div class="file-info mt-2">
                        <a :href="getFileUrl(currentEducation.file)" target="_blank" class="btn btn-sm btn-outline-primary">
                          <i class="icon-feather-external-link"></i> {{ $t('general.view') }}
                        </a>
                      </div>
                    </div>
                    <div v-else-if="currentContentTypeId === 7 && currentEducation.file" class="preview-item">
                      <video :src="getFileUrl(currentEducation.file)"
                             controls
                             class="w-100"
                             style="max-height: 400px; border-radius: 8px;"></video>
                    </div>
                    <div v-else-if="currentContentTypeId === 3 && currentEducation.file" class="preview-item">
                      <a :href="getFileUrl(currentEducation.file)" target="_blank" class="btn btn-primary">
                        <i class="icon-feather-file-text"></i> {{ $t('general.view_pdf') }}
                      </a>
                    </div>
                    <div v-else-if="currentContentTypeId === 6 && currentEducation.file" class="preview-item">
                      <audio :src="getFileUrl(currentEducation.file)" controls class="w-100"></audio>
                    </div>
                    <div v-else-if="currentContentTypeId === 4 && currentEducation.file" class="preview-item">
                      <a :href="getFileUrl(currentEducation.file)" target="_blank" class="btn btn-primary">
                        <i class="icon-feather-file-text"></i> {{ $t('general.view_word') }}
                      </a>
                    </div>
                    <div v-else-if="currentContentTypeId === 5 && currentEducation.file" class="preview-item">
                      <a :href="getFileUrl(currentEducation.file)" target="_blank" class="btn btn-primary">
                        <i class="icon-feather-file-text"></i> {{ $t('general.view_pptx') }}
                      </a>
                    </div>
                    <div v-else-if="currentEducation.file" class="preview-item">
                      <a :href="getFileUrl(currentEducation.file)" target="_blank" class="btn btn-outline-primary">
                        <i class="icon-feather-download"></i> {{ $t('general.click_to_download_file') }}
                      </a>
                    </div>
                    <div v-else-if="currentEducation.training_content && !showUrlArea && !showInformationContentArea" class="preview-item">
                      <a :href="getFileUrl(currentEducation.training_content)" target="_blank" class="btn btn-primary">
                        <i class="icon-feather-external-link"></i> {{ $t('general.click_to_view_file') }}
                      </a>
                    </div>
                  </template>

                  <template v-if="showUrlArea && currentEducation">
                    <div class="preview-item">
                      <label class="form-label"><strong>{{ $t('general.link_address') }}:</strong></label>
                      <div class="alert alert-light border">
                        <a :href="currentEducation.training_content" target="_blank">
                          {{ currentEducation.training_content }}
                          <i class="icon-feather-external-link ml-1"></i>
                        </a>
                      </div>
                    </div>
                  </template>

                  <template v-if="showInformationContentArea && currentEducation">
                    <div class="preview-item">
                      <label class="form-label"><strong>{{ $t('general.information_content') }}:</strong></label>
                      <div class="alert alert-light border" v-html="currentEducation.training_content || $t('general.no_content')"></div>
                    </div>
                  </template>
                </div>
              </template>

              <template v-if="!isReadOnly && showFileUpload">
                <document-file-upload :acceptable-file-type="effectiveAcceptableFileType"
                                      :content-type-key="currentContentType ? currentContentType.key : ''"
                                      :existing-file="isEditing ? currentEducation?.file : null"
                                      :file-category-id="currentContentTypeId"
                                      :icon="fileUploadIcon"
                                      :is-edit-mode="isEditing"
                                      @file-uploaded="handleFileUploaded"
                />
                <div v-if="preview" id="preview" class="filePreview" v-html="preview"></div>
                <div v-if="videoPreview" id="preview" v-html="videoPreview"></div>
                <div v-if="audioPreview" id="preview" v-html="audioPreview"></div>
              </template>

              <template v-if="!isReadOnly && showUrlArea">
                <sf-input v-model="currentEducationUrl"
                          :label="$t('general.link_address')"
                          :placeholder="$t('general.link_address')"
                          :required="true"
                          name="url"
                          type="text"
                />
              </template>

              <template v-if="!isReadOnly && !isEditing && showInformationContentArea">
                <document-information-content v-model="data.information_content"/>
              </template>
              <template v-if="!isReadOnly && isEditing && showInformationContentArea">
                <document-information-content v-model="currentEducation.training_content"/>
              </template>
            </div>
          </div>
        </div>
        <template v-slot:footer>
          <div class="catalog-modal-footer">
            <button class="uk-button uk-button-outline-default catalog-modal-footer__btn catalog-modal-footer__btn--back" type="button" @click="closeModal">
              <i class="icon-arrow-left"></i> {{ $t('general.back') }}
            </button>
            <button class="uk-button uk-button-primary catalog-modal-footer__btn catalog-modal-footer__btn--save" type="button"
                    @click="saveData(content.id)">
              <i class="icon-feather-save"></i> {{ $t("general.save") }}
            </button>
          </div>
        </template>

      </AppModal>
    </div>
  </div>
</template>

<script>
import store from "@/core/services";
import content_categories_module, {
  GET_FILE_TYPES,
  FILE_TYPES_URL,
  MODULE_NAME as CONTENT_CATEGORIES_MODULE,
  FILE_TYPES,
} from "@/core/services/store/content_categories.module";

import training_section_module, {
  BASE_URL as TRAINING_SECTION_BASE_URL,
  GET_ITEMS as GET_TRAINING_SECTIONS,
  MODULE_NAME as TRAINING_SECTION_MODULE,
  ITEMS as TRAINING_SECTIONS,
  DELETE_ITEM as TRAINING_SECTION_DELETE,
} from "@/core/services/store/training_section.module";

import training_content_module, {
  BASE_URL as TRAINING_CONTENT_BASE_URL,
  DELETE_ITEM as TRAINING_CONTENT_DELETE,
  MODULE_NAME as TRAINING_CONTENT_MODULE,
} from "@/core/services/store/training_content.module";

import Multiselect from "vue-multiselect";
import Swal from "sweetalert2";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import { DocumentInformationContent, DocumentFileUpload } from "@/view/company/Content/components";
import SfInput from "@/view/components/ui/SfInput.vue";
import EducationItemCard from "./EducationItemCard.vue";
import AppModal from "@/view/components/ui/AppModal.vue";

const _CONTENT_CATEGORIES_MODULE = CONTENT_CATEGORIES_MODULE;
const _TRAINING_SECTION_MODULE = TRAINING_SECTION_MODULE;
const _TRAINING_CONTENT_MODULE = TRAINING_CONTENT_MODULE;

export default {
  name: "CatalogContentAdd",
  components: {
    Multiselect,
    MultipleSelectInput,
    DocumentInformationContent,
    DocumentFileUpload,
    SfInput,
    EducationItemCard,
    AppModal
  },
  props: {
    isEditMode: {
      type: Boolean,
      default: false
    },
    catalogSectionData: {
      type: Array,
      default: () => []
    }
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_CONTENT_CATEGORIES_MODULE, content_categories_module);
    registerStoreModule(_TRAINING_SECTION_MODULE, training_section_module);
    registerStoreModule(_TRAINING_CONTENT_MODULE, training_content_module);
  },
  data() {
    return {
      form: {
        contents: [],
      },
      NewSectionIds: [],
      preview: null,
      is_demo: false,
      content_type_id: null,
      data: {
        use_content_point: false,
        score: 100,
        information_content: ""
      },
      imageArea: false,
      scormArea: false,
      urlArea: false,
      videoArea: false,
      audioArea: false,
      informationContentArea: false,
      videoPreview: null,
      audioPreview: null,
      errorMessage: '',
      // Unified modal variables
      currentEducation: null,
      currentContentId: null,
      currentEducationIndex: null,
      isEditingMode: false,
      contentLink: null,
      docKeyAreas: {
        imageArea: [
          "documents.visual_document",
          "documents.pdf_document",
          "documents.word_document",
          "documents.powerpoint_document",
          "documents.scorm_package",
        ],
        urlArea: [
          "documents.video_url",
          "documents.url",
          "documents.webinar",
          "documents.classroom_education",
        ],
        videoArea: ["documents.video_document"],
        audioArea: ["documents.audio_document"],
      },
      activeModalId: null
    };
  },
  computed: {
    content_types: {
      get() {
        return store.getters[_CONTENT_CATEGORIES_MODULE + "/" + FILE_TYPES];
      },
      set(value) {
      },
    },
    training_sections: {
      get() {
        return store.getters[_TRAINING_SECTION_MODULE + "/" + TRAINING_SECTIONS];
      },
      set(value) {
      },
    },
    formattedTrainingSection() {
      if (!this.isEditMode) return [];

      let results = [];
      if (this.training_sections === null || !("data" in this.training_sections)) {
        return [];
      }

      this.training_sections.data.forEach((item) => {
        let educations = [];

        item.training_content.forEach((education) => {
          educations.push({
            name: education.name,
            training_content_type_id: education.training_content_type_id,
            training_file_type_id: education.training_file_type_id,
            content_type_id: education.training_file_type_id,
            id: education.id,
            score: education.score,
            duration: education.duration,
            training_content: education.training_content,
            content_link: education.content_link,
            is_demo: education.is_demo,
            use_content_point: education.use_content_point,
            file: education.file, // Add file object for file-based content
          });
        });

        results.push({
          name: item.name,
          id: item.id,
          educations: educations,
          show_order: item.show_order,
        });
      });

      results = results.slice().sort(function (a, b) {
        return a.show_order - b.show_order;
      });

      return results;
    },
    formattedContentTypes: {
      get() {
        let results = [];

        // Katalog eğitimde desteklenmeyen içerik türleri
        const excludedContentTypes = [
          'documents.jitsi',
          'documents.mission',
          'documents.check_list_item',
          'documents.classroom_education'
        ];

        if (this.content_types === null) return [];
        this.content_types.forEach((item) => {
          if (item && !excludedContentTypes.includes(item.key_name)) {
            results.push({
              icon: item.icon,
              name: item.name,
              id: item.id,
              key: item.key_name,
              accept: item.accept,
            });
          }
        });
        return results;
      },
      set(value) {
      },
    },
    isEditing() {
      return !!(this.isEditingMode && this.currentEducation);
    },
    // Veritabanından gelen içerikler için read-only mod (sadece önizleme)
    isReadOnly() {
      return !!(this.isEditing && this.currentEducation?.id);
    },
    currentContentTypeId() {
      if (this.isEditing) {
        const typeId = this.currentEducation.training_file_type_id || this.currentEducation.content_type_id;
        // content_type_id obje olarak gelebilir (yeni eklenen içeriklerde)
        return typeof typeId === 'object' ? typeId.id : typeId;
      }
      return this.content_type_id;
    },
    currentContentType() {
      if (!this.currentContentTypeId) return null;
      return this.getContentTypeById(this.currentContentTypeId);
    },
    effectiveAcceptableFileType() {
      return this.getAcceptableFileType(this.currentContentType);
    },
    showImageArea() {
      if (!this.currentContentType) return false;
      return this.docKeyAreas.imageArea.includes(this.currentContentType.key);
    },
    showUrlArea() {
      if (!this.currentContentType) return false;
      return this.docKeyAreas.urlArea.includes(this.currentContentType.key);
    },
    showVideoArea() {
      if (!this.currentContentType) return false;
      return this.docKeyAreas.videoArea.includes(this.currentContentType.key);
    },
    showAudioArea() {
      if (!this.currentContentType) return false;
      return this.docKeyAreas.audioArea.includes(this.currentContentType.key);
    },
    showScormArea() {
      if (!this.currentContentType) return false;
      return this.currentContentType.key === "documents.scorm_package";
    },
    showInformationContentArea() {
      if (!this.currentContentType) return false;
      return this.currentContentType.key === "documents.information_content";
    },
    // File upload için tek bir kontrol
    showFileUpload() {
      if (!this.currentContentType) return false;
      const fileUploadTypes = [
        "documents.visual_document",
        "documents.pdf_document",
        "documents.word_document",
        "documents.powerpoint_document",
        "documents.scorm_package",
        "documents.video_document",
        "documents.audio_document"
      ];
      return fileUploadTypes.includes(this.currentContentType.key);
    },
    // İçerik türüne göre icon belirleme
    fileUploadIcon() {
      if (!this.currentContentType) return "icon-feather-file-plus";

      const iconMap = {
        "documents.video_document": "icon-feather-film",
        "documents.audio_document": "icon-feather-mic",
        "documents.scorm_package": "icon-feather-file-plus",
        "documents.visual_document": "icon-feather-image",
        "documents.pdf_document": "icon-feather-file-text",
        "documents.word_document": "icon-feather-file-text",
        "documents.powerpoint_document": "icon-feather-file-text"
      };

      return iconMap[this.currentContentType.key] || "icon-feather-file-plus";
    },
    currentEducationName: {
      get() {
        return this.isEditing ? (this.currentEducation?.name || '') : (this.data.name || '');
      },
      set(value) {
        if (this.isEditing && this.currentEducation) {
          this.currentEducation.name = value;
        } else {
          this.data.name = value;
        }
      }
    },
    currentEducationScore: {
      get() {
        return this.isEditing ? (this.currentEducation?.score || 0) : (this.data.score || 100);
      },
      set(value) {
        if (this.isEditing && this.currentEducation) {
          this.currentEducation.score = value;
        } else {
          this.data.score = value;
        }
      }
    },
    currentEducationDuration: {
      get() {
        return this.isEditing ? (this.currentEducation?.duration || 0) : (this.data.duration || 0);
      },
      set(value) {
        if (this.isEditing && this.currentEducation) {
          this.currentEducation.duration = value;
        } else {
          this.data.duration = value;
        }
      }
    },
    currentEducationUseContentPoint: {
      get() {
        return this.isEditing ? (this.currentEducation?.use_content_point || false) : (this.data.use_content_point || false);
      },
      set(value) {
        if (this.isEditing && this.currentEducation) {
          this.currentEducation.use_content_point = value;
        } else {
          this.data.use_content_point = value;
        }
      }
    },
    currentEducationUrl: {
      get() {
        return this.isEditing ? (this.currentEducation?.training_content || '') : (this.data.url || '');
      },
      set(value) {
        if (this.isEditing && this.currentEducation) {
          this.currentEducation.training_content = value;
        } else {
          this.data.url = value;
        }
      }
    },
    currentEducationIsDemo: {
      get() {
        if (this.isEditing) {
          return this.currentEducation?.is_demo === 1 || this.currentEducation?.is_demo === true;
        }
        return this.is_demo;
      },
      set(value) {
        if (this.isEditing && this.currentEducation) {
          this.currentEducation.is_demo = value ? 1 : 0;
        } else {
          this.is_demo = value;
        }
      }
    },
  },
  methods: {
    createContentGroup() {
      Swal.fire({
        title: this.$t('general.content_title'),
        input: "text",
        inputAttributes: {
          autocapitalize: "off",
        },
        showCancelButton: true,
        confirmButtonText: this.$t('general.create'),
        confirmButtonColor: "#ae71ff",
        cancelButtonText: this.$t('general.cancel'),
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
      }).then((result) => {
        if (result.isConfirmed) {
          if (result.value.length != 0) {
            let length = this.form.contents.length;
            let newId = length + 1;
            // Edit modunda yeni eklenen section'ları takip et
            if (this.isEditMode) {
              this.NewSectionIds.push(newId);
            }
            this.form.contents.push({
              id: newId,
              name: result.value,
              educations: [],
              status: this.isEditMode ? "new" : undefined,
            });
            this.setParent();
          } else {
            Swal.fire({
              icon: "error",
              title: this.$t('general.error'),
              text: this.$t('general.warning_enter_a_value'),
              confirmButtonColor: "#ae71ff",
              confirmButtonText: this.$t('general.ok')
            });
          }
        }
      });
    },
    closeModal() {
      this.cleanModal();
    },
    cleanModal() {
      // Reset form data
      this.preview = null;
      this.audioPreview = null;
      this.videoPreview = null;
      this.is_demo = false;
      this.data = {
        use_content_point: false,
        score: 100,
        information_content: "",
        url: ""
      };
      this.content_type_id = null;

      // Reset unified modal variables
      this.currentEducation = null;
      this.currentContentId = null;
      this.currentEducationIndex = null;
      this.isEditingMode = false;
      this.contentLink = null;
      this.errorMessage = '';
      this.activeModalId = null;
    },
    openEditModal(contentId, educationIndex) {
      this.cleanModal();
      this.currentContentId = contentId;
      this.currentEducationIndex = educationIndex;
      this.isEditingMode = true;

      const contentItem = this.form.contents.find(item => item.id === contentId);
      if (contentItem && contentItem.educations[educationIndex]) {
        const originalEducation = contentItem.educations[educationIndex];
        this.currentEducation = JSON.parse(JSON.stringify(originalEducation));

        // media File objesi olduğu için JSON.parse/stringify ile kayboluyor, ayrıca kopyala
        if (originalEducation.media && originalEducation.media instanceof File) {
          this.data.media = originalEducation.media;
          this.createMediaPreview(originalEducation.media);
        }

        let training_file_type_id = this.currentEducation.training_file_type_id || this.currentEducation.content_type_id;
        // content_type_id obje olarak gelebilir (yeni eklenen içeriklerde)
        if (typeof training_file_type_id === 'object') {
          training_file_type_id = training_file_type_id.id;
        }
        if (training_file_type_id) {
          const ContentType = this.getContentTypeById(training_file_type_id);
          if (ContentType) {
            // Set content link based on content type
            if (this.docKeyAreas.urlArea.includes(ContentType.key)) {
              this.contentLink = this.currentEducation.training_content || '';
            } else if (this.docKeyAreas.imageArea.includes(ContentType.key)) {
              if (this.currentEducation.file?.file_link) {
                this.contentLink = this.currentEducation.file.file_link;
              } else if (this.currentEducation.content_link) {
                this.contentLink = this.currentEducation.content_link;
              } else if (this.currentEducation.training_content) {
                const contentUrl = this.currentEducation.training_content;
                if (contentUrl.startsWith('http://') || contentUrl.startsWith('https://')) {
                  this.contentLink = contentUrl;
                } else {
                  this.contentLink = `https://api.zorunluegitimler.com/storage/${contentUrl}`;
                }
              }
            } else if (this.docKeyAreas.videoArea.includes(ContentType.key) || this.docKeyAreas.audioArea.includes(ContentType.key)) {
              if (this.currentEducation.file?.file_link) {
                this.contentLink = this.currentEducation.file.file_link;
              } else if (this.currentEducation.content_link) {
                this.contentLink = this.currentEducation.content_link;
              } else if (this.currentEducation.training_content) {
                const contentUrl = this.currentEducation.training_content;
                if (contentUrl.startsWith('http://') || contentUrl.startsWith('https://')) {
                  this.contentLink = contentUrl;
                } else {
                  this.contentLink = `https://api.zorunluegitimler.com/storage/${contentUrl}`;
                }
              }
            }
          }
        }
      }

      // Modal'ı aç
      this.activeModalId = contentId;
    },
    checkEditedEducationValidation(education) {
      if (!education.name) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.content_title'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if (!education.duration) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.duration'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if (!education.use_content_point && (!education.score || parseInt(education.score) < 0 || parseInt(education.score) > 100)) {
        this.fireError({ message: this.$t("general.content_score") });
        return false;
      }
      return true;
    },
    toggleModal(contentId) {
      this.cleanModal();
      this.currentContentId = contentId;
      this.isEditingMode = false;
      this.activeModalId = contentId;

      // Initialize selectpicker if needed
      this.$nextTick(() => {
        setTimeout(() => {
          if ($('.selectpickerFileCategory').length) {
            $('.selectpickerFileCategory').selectpicker();
          }
        }, 300);
      });
    },
    saveData(content_id) {
      if (this.isEditing) {
        // Edit mode
        const contentItem = this.form.contents.find(item => item.id === content_id);
        if (contentItem && contentItem.educations[this.currentEducationIndex]) {
          const education = contentItem.educations[this.currentEducationIndex];

          // Update education with currentEducation data
          education.name = this.currentEducation.name;
          education.score = this.currentEducation.score;
          education.duration = this.currentEducation.duration;
          education.use_content_point = this.currentEducation.use_content_point;
          education.is_demo = this.currentEducation.is_demo;

          // Update training_content for URL-based content
          if (this.showUrlArea) {
            education.training_content = this.currentEducation.training_content;
          }

          // use_content_point değeri sadece scorm seçiminde aktif olacak, score 0 yapılacak
          let typeId = education.training_file_type_id || education.content_type_id;
          if (typeof typeId === 'object') {
            typeId = typeId.id;
          }
          if (typeId === 1) {
            education.use_content_point = education.use_content_point === true ? 1 :
                education.use_content_point === false ? 0 :
                    education.use_content_point;
            if (education.use_content_point === 1) {
              education.score = 0;
            }
          }

          // Eğer yeni medya verisi varsa güncelle
          if (this.data.media && this.data.media instanceof File) {
            education.media = this.data.media;
          }

          // Eğitim doğrulama kontrolü
          if (!this.checkEditedEducationValidation(education)) {
            return;
          }
        }
      } else {
        // Add mode
        const isValid = this.checkValidations();
        if (!isValid) {
          return;
        }

        const contentItem = this.form.contents.find(item => item.id === content_id);
        if (contentItem) {
          let newData = { ...this.data };
          newData.is_demo = this.is_demo == true ? 1 : 0;
          newData.score = newData.use_content_point === true ? 0 : newData.score;
          newData.content_type_id = this.content_type_id;
          newData.use_content_point = newData.use_content_point === true ? 1 : 0;

          // URL için training_content'e kaydet
          if (this.showUrlArea && newData.url) {
            newData.training_content = newData.url;
          }

          contentItem.educations.push(newData);
        }
      }

      // Reset and close
      this.preview = null;
      this.audioPreview = null;
      this.videoPreview = null;
      this.is_demo = false;
      this.data = {
        use_content_point: false,
        score: 100,
        information_content: "",
        url: ""
      };
      this.setParent();
      this.closeModal();
    },
    handleFileUploaded(file) {
      if (!file) return;

      // Düzenleme modunda currentContentTypeId kullan, yoksa content_type_id
      const contentTypeId = this.currentContentTypeId || this.content_type_id;
      const contentType = this.getContentTypeById(contentTypeId);
      if (!contentType) return;

      const acceptableType = this.getAcceptableFileType(contentType);
      if (!this.compareTypeCompatibality(file, acceptableType)) {
        Swal.fire({
          icon: "error",
          title: this.$t('general.error'),
          text: this.$t('general.please_select_valid_file'),
          confirmButtonColor: "#ae71ff",
          confirmButtonText: this.$t('general.ok'),
        });
        return;
      }

      this.data.media = file;

      // Preview oluştur (createMediaPreview metodunu kullan)
      this.createMediaPreview(file);
    },

    compareTypeCompatibality(file, acceptableType) {
      return (
          acceptableType.includes(file.type) ||
          acceptableType.includes(file.name.split(".").pop()) ||
          (acceptableType.split("/")[0] == file.type.split("/")[0] &&
              file.type.split("/")[0].toLowerCase() != "application")
      );
    },
    getAcceptableFileType(contentType) {
      if (!contentType) return "";
      if (contentType.key === "documents.powerpoint_document") {
        return ".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation";
      }
      return contentType.accept || "";
    },
    getContentTypeById(id) {
      if (typeof id.id !== "undefined") {
        id = id.id;
      }
      let ContentType;
      this.formattedContentTypes.forEach((item) => {
        if (item.id === id) {
          ContentType = item;
        }
      });

      return ContentType;
    },
    changeOption(id) {
      let self = this;
      let ContentType = this.getContentTypeById(id);
      if (typeof ContentType.key !== "undefined") {
        self.imageArea = false;
        self.urlArea = false;
        self.videoArea = false;
        self.audioArea = false;
        self.scormArea = false;
        self.informationContentArea = false;
        self.preview = null;
        self.videoPreview = null;
        self.audioPreview = null;
        switch (ContentType.key) {
          case "documents.visual_document":
          case "documents.word_document":
          case "documents.pdf_document":
          case "documents.powerpoint_document":
            self.imageArea = true;
            break;
          case "documents.scorm_package":
            self.scormArea = true;
            break;
          case "documents.video_url":
          case "documents.url":
          case "documents.anzera_education":
          case "documents.webinar":
          case "documents.classroom_education":
            self.urlArea = true;
            break;
          case "documents.video_document":
            self.videoArea = true;
            break;
          case "documents.audio_document":
            self.audioArea = true;
            break;
          case "documents.information_content":
            self.informationContentArea = true;
            break;
        }
      }
    },
    setParent() {
      this.$emit("getData", this.form.contents);
    },
    checkValidations() {
      if (!this.content_type_id) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.content_type'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if (!this.data?.name || this.data.name?.trim() === "") {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.content_title'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if (!this.data.use_content_point && (!this.data?.score || parseInt(this.data?.score) < 0 || parseInt(this.data?.score) > 100)) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.content_score'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if (!this.data?.duration || parseInt(this.data?.duration) <= 0) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.duration'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if (this.showUrlArea && (!this.data.url || this.data.url.trim() === "")) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.link_address'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if ((this.showImageArea || this.showVideoArea || this.showAudioArea || this.showScormArea) && !this.data.media) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.upload'));
        this.fireError({ message: this.errorMessage });
        return false;
      } else if (this.showInformationContentArea && (!this.data.information_content || this.data.information_content.trim() === "")) {
        this.errorMessage = this.$t("general.area_required").replace(":NAME", this.$t('general.information_content'));
        this.fireError({ message: this.errorMessage });
        return false;
      }
      return true;
    },
    fireError({ message }) {
      Swal.fire({
        icon: "error",
        title: this.$t('general.error'),
        text: message,
        confirmButtonColor: "#ae71ff",
        confirmButtonText: this.$t('general.ok'),
      })
    },
    deleteEducation(content_index, education_index) {
      let self = this;

      Swal.fire({
        title: this.$t('general.sure_to_delete'),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel'),
        confirmButtonColor: "#e31748",
      }).then((result) => {
        if (result.isConfirmed) {
          // Edit modunda ve mevcut education ise API'den sil
          if (
              self.isEditMode &&
              self.form.contents[content_index].educations[education_index].hasOwnProperty("id")
          ) {
            var training_content_id =
                self.form.contents[content_index].educations[education_index].id;
            store.dispatch(
                _TRAINING_CONTENT_MODULE + "/" + TRAINING_CONTENT_DELETE,
                {
                  url: TRAINING_CONTENT_BASE_URL + "/" + training_content_id,
                }
            );
          }

          self.form.contents[content_index].educations.splice(education_index, 1);
          self.setParent();
        }
      });
    },
    deleteGroupById(content_id) {
      let self = this;

      Swal.fire({
        title: this.$t('general.sure_to_delete'),
        html: this.$t('general.sure_to_delete_group'),
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel'),
        confirmButtonColor: "#e31748",
      }).then((result) => {
        if (result.isConfirmed) {
          // Edit modunda ve yeni eklenmemiş section ise API'den sil
          if (self.isEditMode && !self.NewSectionIds.includes(content_id)) {
            store.dispatch(
                _TRAINING_SECTION_MODULE + "/" + TRAINING_SECTION_DELETE,
                {
                  url: TRAINING_SECTION_BASE_URL + "/" + content_id,
                }
            );
          }

          self.form.contents.forEach((item, index) => {
            if (item.id == content_id) {
              self.form.contents.splice(index, 1);
            }
          });
          self.setParent();
        }
      });
    },
    bytesToSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    getFileUrl(fileOrPath) {
      if (!fileOrPath) return '';

      // Eğer file objesi ise file_link'i al
      const url = typeof fileOrPath === 'object' ? fileOrPath.file_link : fileOrPath;

      if (!url) return '';

      // Zaten tam URL ise direkt döndür
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }

      // Değilse base URL ekle
      return `https://api.zorunluegitimler.com/storage/${url}`;
    },
    createMediaPreview(file) {
      if (!file) return;

      const fileInfoHtml =
          '<div class="file-info">' +
          "<div>" + this.$t('general.format') + ": <span>" + file.type + "</span></div>" +
          "<div>" + this.$t('general.file_name') + ": <span>" + file.name + "</span></div>" +
          "<div>" + this.$t('general.size') + ": <span>" + this.bytesToSize(file.size) + "</span></div>" +
          "</div>";

      // Video dosyası
      if (file.type.startsWith('video/')) {
        this.videoPreview =
            '<div class="file-info">' +
            "<div>" + this.$t('general.format') + ": <span>" + file.type + ' <i class="uil-video" style="font-size: 14px"></i></span></div>' +
            "<div>" + this.$t('general.video_file_name') + ": <span>" + file.name + "</span></div>" +
            "<div>" + this.$t('general.size') + ": <span>" + this.bytesToSize(file.size) + "</span></div>" +
            "</div>";
        this.preview = null;
        this.audioPreview = null;
        return;
      }

      // Audio dosyası
      if (file.type.startsWith('audio/')) {
        this.audioPreview =
            '<div class="file-info">' +
            "<div>" + this.$t('general.format') + ": <span>" + file.type + ' <i class="uil-microphone" style="font-size: 14px"></i></span></div>' +
            "<div>" + this.$t('general.audio_file_name') + ": <span>" + file.name + "</span></div>" +
            "<div>" + this.$t('general.size') + ": <span>" + this.bytesToSize(file.size) + "</span></div>" +
            "</div>";
        this.preview = null;
        this.videoPreview = null;
        return;
      }

      // Image/Document/SCORM
      this.preview = fileInfoHtml + "<br>";

      // Görsel dosyalar için image preview
      if (file.type.startsWith('image/')) {
        this.preview += '<div><img src="' + URL.createObjectURL(file) + '" style="max-width: 200px; max-height: 150px; object-fit: contain;" /></div>';
      }

      this.videoPreview = null;
      this.audioPreview = null;
    },
  },
  watch: {
    formattedTrainingSection: {
      handler(newVal) {
        if (this.isEditMode && newVal && newVal.length > 0) {
          this.form.contents = JSON.parse(JSON.stringify(newVal));
          this.setParent();
        }
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    store.dispatch(_CONTENT_CATEGORIES_MODULE + "/" + GET_FILE_TYPES, {
      url: FILE_TYPES_URL,
      headers: this.apiHeaders
    });

    // Edit modunda training sections'ı yükle
    if (this.isEditMode && this.$route.params.id) {
      store.dispatch(_TRAINING_SECTION_MODULE + "/" + GET_TRAINING_SECTIONS, {
        url: TRAINING_SECTION_BASE_URL + "?training_id=" + this.$route.params.id,
      });
    }
  },
};
</script>
<style scoped>
/* ─── Base ─── */
input[type="button"],
input[type="submit"],
span.btn,
.btn {
  display: inline-block;
}

.required {
  color: #ed5252;
}

/* ─── Add content group button ─── */
.catalog-content-area__add-group-row {
  margin-bottom: 12px;
}

.catalog-content-area__add-group-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.catalog-content-area__section {
  margin-bottom: 8px;
}

/* ─── Section header (accordion header) ─── */
.catalog-section-header {
  overflow: hidden;
  min-width: 0;
}

.catalog-section-header__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.catalog-section-header__icon {
  flex-shrink: 0;
  font-size: 150%;
  text-align: center;
  color: #64748b;
}

.catalog-section-header__name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.catalog-section-header__name .uk-accordion-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.catalog-section-header__actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.catalog-section-header__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #fff;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.catalog-section-header__action-btn:active {
  transform: scale(0.98);
  opacity: 0.85;
}

.catalog-section-header__action-btn--delete {
  background-color: #ed5252 !important;
  border-color: #ed5252 !important;
  color: #fff !important;
}

/* ─── Modal inner form ─── */
.catalog-modal-form {
  width: 100%;
}

.catalog-modal-form__row {
  display: flex;
  gap: 24px;
}

.catalog-modal-form__sidebar {
  flex: 0 0 33.333%;
  max-width: 33.333%;
  min-width: 0;
}

.catalog-modal-form__content {
  flex: 1;
  min-width: 0;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.catalog-modal-field {
  margin-bottom: 8px;
}

.catalog-modal-field__inner {
  width: 100%;
}

.catalog-modal-field__label {
  display: block;
  margin-bottom: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
}

.catalog-modal-form__upload-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
}

/* ─── Content preview area ─── */
.content-preview-area {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #e9ecef;
}

.preview-item {
  margin-bottom: 15px;
}

.preview-item:last-child {
  margin-bottom: 0;
}

.file-info {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* ─── Upload area bridge (DocumentFileUpload) ─── */
.catalog-modal-form__content ::v-deep .document-file-upload .dragBox {
  width: auto !important;
  padding: 10px;
}

.catalog-modal-form__content ::v-deep .document-file-upload .uploadOuter {
  padding: 16px;
}

.catalog-modal-form__content ::v-deep .document-file-upload .file-info {
  border-radius: 8px;
}

/* ─── Information content (Quill editor) overflow fix ─── */
.catalog-modal-form__content ::v-deep .document-information-content {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  margin: 0;
}

.catalog-modal-form__content ::v-deep .document-information-content .editor-wrapper {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
}

.catalog-modal-form__content ::v-deep .document-information-content .ql-container {
  max-width: 100%;
  overflow: hidden;
}

.catalog-modal-form__content ::v-deep .document-information-content .ql-editor {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;
  max-width: 100%;
  overflow-x: hidden;
}

.catalog-modal-form__content ::v-deep .document-information-content .ql-toolbar {
  max-width: 100%;
  overflow-x: auto;
  flex-wrap: wrap;
}

/* ─── Modal footer ─── */
.catalog-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.catalog-modal-footer__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 40px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.catalog-modal-footer__btn:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* ─── Mobile (≤ 959px) ─── */
@media (max-width: 959px) {
  /* Modal full-screen on mobile */
  .catalog-content-area__section ::v-deep .app-modal-overlay {
    padding: 0;
  }

  .catalog-content-area__section ::v-deep .app-modal {
    width: 100% !important;
    max-width: 100% !important;
    height: 100vh;
    max-height: 100vh !important;
    border-radius: 0;
  }

  .catalog-content-area__section ::v-deep .app-modal-header {
    border-radius: 0;
  }

  .catalog-content-area__section ::v-deep .app-modal-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* Add group button */
  .catalog-content-area__add-group-btn {
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
  }

  /* Section header: stack vertically */
  .catalog-section-header {
    overflow: visible;
  }

  .catalog-section-header__row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .catalog-section-header__icon {
    display: none;
  }

  .catalog-section-header__name {
    width: 100%;
  }

  .catalog-section-header__name .uk-accordion-title {
    white-space: normal;
    word-break: break-word;
    font-size: 16px;
  }

  .catalog-section-header__actions {
    width: 100%;
    flex-wrap: wrap;
    gap: 8px;
  }

  .catalog-section-header__action-btn {
    min-height: 44px;
    padding: 10px 16px;
    font-size: 16px;
    border-radius: 10px;
    flex: 1;
  }

  /* Modal form: stack sidebar and content */
  .catalog-modal-form__row {
    flex-direction: column;
    gap: 16px;
  }

  .catalog-modal-form__sidebar {
    flex: none;
    max-width: 100%;
  }

  .catalog-modal-form__content {
    width: 100%;
  }

  .catalog-modal-field__label {
    font-size: 16px;
  }

  .catalog-modal-form__upload-title {
    font-size: 16px;
  }

  /* Deep styles for form inputs inside modal */
  .catalog-modal-form__sidebar ::v-deep .form-control,
  .catalog-modal-form__sidebar ::v-deep input[type="text"],
  .catalog-modal-form__sidebar ::v-deep input[type="number"],
  .catalog-modal-form__sidebar ::v-deep select {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
    border-radius: 10px;
  }

  .catalog-modal-form__sidebar ::v-deep .multiselect {
    min-height: 48px;
    font-size: 16px;
  }

  .catalog-modal-form__sidebar ::v-deep .multiselect__tags {
    min-height: 48px;
    padding: 8px 40px 0 12px;
    font-size: 16px;
    border-radius: 10px;
  }

  .catalog-modal-form__sidebar ::v-deep .multiselect__single {
    font-size: 16px;
    line-height: 1.4;
  }

  .catalog-modal-form__sidebar ::v-deep .multiselect__select {
    height: 46px;
  }

  /* Content area mobile padding */
  .catalog-modal-form__content {
    padding: 12px;
  }

  /* DocumentFileUpload bridge mobile */
  .catalog-modal-form__content ::v-deep .document-file-upload .dragBox {
    width: 100% !important;
    padding: 16px;
    font-size: 16px;
    line-height: 1.4;
    min-height: 80px;
    display: block;
  }

  .catalog-modal-form__content ::v-deep .document-file-upload .uploadOuter {
    padding: 16px 0;
  }

  .catalog-modal-form__content ::v-deep .document-file-upload .uploadOuter .btn {
    min-height: 48px;
    padding: 12px 24px;
    font-size: 16px;
    border-radius: 10px;
  }

  .catalog-modal-form__content ::v-deep .document-file-upload .file-info {
    flex-direction: column;
    gap: 8px;
  }

  /* SfInput / URL input inside content area */
  .catalog-modal-form__content ::v-deep .form-control,
  .catalog-modal-form__content ::v-deep input[type="text"] {
    min-height: 48px;
    height: 48px;
    padding: 12px 16px;
    font-size: 16px;
    line-height: 1.4;
    border-radius: 10px;
  }

  /* Information content (Quill editor) mobile */
  .catalog-modal-form__content ::v-deep .document-information-content {
    margin-left: 0;
    margin-right: 0;
    padding: 0;
  }

  .catalog-modal-form__content ::v-deep .document-information-content .editor-wrapper {
    padding: 0;
    margin: 0;
  }

  .catalog-modal-form__content ::v-deep .document-information-content .ql-toolbar {
    padding: 6px 8px;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    flex-wrap: wrap;
  }

  .catalog-modal-form__content ::v-deep .document-information-content .ql-toolbar .ql-formats {
    margin-right: 8px;
    margin-bottom: 4px;
  }

  .catalog-modal-form__content ::v-deep .document-information-content .ql-editor {
    min-height: 150px;
    padding: 12px;
    font-size: 16px;
  }

  .catalog-modal-form__content ::v-deep .document-information-content .ql-container {
    border-radius: 0 0 10px 10px;
  }

  .catalog-modal-form__content ::v-deep .document-information-content .ql-toolbar.ql-snow {
    border-radius: 10px 10px 0 0;
  }

  /* Content preview area mobile */
  .content-preview-area {
    padding: 16px;
  }

  .file-info {
    flex-direction: column;
    gap: 8px;
  }

  /* Modal footer: side by side */
  .catalog-modal-footer {
    flex-direction: row;
    gap: 8px;
  }

  .catalog-modal-footer__btn {
    flex: 1;
    min-height: 48px;
    padding: 12px 16px;
    font-size: 16px;
  }
}

/* ─── Desktop hover ─── */
@media (hover: hover) and (pointer: fine) {
  .catalog-content-area__add-group-btn:hover,
  .catalog-section-header__action-btn:hover {
    opacity: 0.92;
  }

  .catalog-modal-footer__btn:hover {
    opacity: 0.92;
  }
}

/* ─── Reduced motion ─── */
@media (prefers-reduced-motion: reduce) {
  .catalog-section-header__action-btn:active,
  .catalog-modal-footer__btn:active,
  .catalog-content-area__add-group-btn:active {
    transform: none;
  }
}
</style>


<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style>
.multiselect .multiselect__input {
  border: unset !important;
  border: 0 !important;
  box-shadow: none;
  outline: none;
}
</style>
<style>
.multiselect__tag {
  color: white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  background-color: #3e416d;
}

.multiselect__tag-icon {
  background-color: transparent;
  line-height: 2.2;
}

.multiselect__tag-icon:after {
  color: #fff;
}

.multiselect__tag-icon:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.multiselect__input {
  border: unset !important;
}

.multiselect__tags .multiselect__input {
  border: unset !important;
}
</style>
